import { apiFetch } from './client';
import type { RawStall, RawStallEnvelope, Stall, StallStatus } from './types';

/**
 * Convert one wire-shape stall to the UI shape. Lossless where the data
 * exists; lenient where it doesn't (the backend doesn't yet surface a
 * neighborhood field, so we derive a short label from the address).
 *
 * Status mapping: backend uses `'open' | 'likely' | 'closed'`. We treat
 * `'likely'` as `'open'` in the UI (a stall is considered open unless
 * the latest report says otherwise — the pin still pulses if fresh).
 */
function mapStall(raw: RawStall): Stall {
  const nameTh = raw.name.th;
  const nameEn = raw.name.en ?? raw.name.th;
  const dishes = raw.dishes ?? [];
  // Cheap split: anything with at least one Thai codepoint goes to dishes_th,
  // everything else to dishes_en. Avoids carrying duplicates into both
  // language modes and keeps the UI's bilingual rule simple.
  const thaiRe = /[฀-๿]/;
  const dishes_th: string[] = [];
  const dishes_en: string[] = [];
  for (const d of dishes) {
    if (thaiRe.test(d)) dishes_th.push(d);
    else dishes_en.push(d);
  }
  // Until the backend ships a neighborhood field, derive from address.
  // We trim to the segment AFTER the last comma if there is one, else
  // use the address verbatim.
  const address = raw.address ?? '';
  const lastComma = address.lastIndexOf(',');
  const neighborhood =
    lastComma >= 0 ? address.slice(lastComma + 1).trim() : address.trim();

  const lastReportAt = raw.live_status.last_report_at;
  const ageMs = lastReportAt ? Date.now() - Date.parse(lastReportAt) : null;
  const last_report_mins_ago =
    ageMs !== null && !Number.isNaN(ageMs) ? Math.max(0, Math.round(ageMs / 60_000)) : null;

  // `fresh` = a report landed in the last 5 minutes. Backend status of
  // `'likely'` (still inside the 30-min decay window) by itself isn't enough.
  const fresh = last_report_mins_ago !== null && last_report_mins_ago <= 5;

  const status: StallStatus =
    raw.live_status.state === 'closed'
      ? 'closed'
      : raw.live_status.state === 'open' || raw.live_status.state === 'likely'
        ? 'open'
        : 'unknown';

  return {
    id: raw.id,
    name_en: nameEn,
    name_th: nameTh,
    category: raw.category,
    dishes_en,
    dishes_th,
    neighborhood_en: neighborhood,
    neighborhood_th: neighborhood,
    address_en: address,
    address_th: address,
    lat: raw.location.lat,
    lng: raw.location.lng,
    status,
    fresh,
    just_opened: false,
    last_report_mins_ago,
    open_votes_30m: 0,
    closed_votes_30m: 0,
    rating: raw.rating.count > 0 ? raw.rating.average : 0,
    reviews: raw.rating.count,
    hours: null,
    photo_url: raw.thumbnail_url ?? (raw.photos?.[0] ?? null),
  };
}

function mapEnvelope(env: RawStallEnvelope): { stalls: Stall[] } {
  return { stalls: env.stalls.map(mapStall) };
}

export interface StallSubmissionInput {
  name_th: string;
  name_en?: string;
  category?: string;
  dishes?: string[];
  lat: number;
  lng: number;
  address?: string;
  photo_media_key: string;
}

export interface StallSubmissionResponse {
  stall_id: string;
  auto_published: boolean;
  message_en?: string;
  message_th?: string;
  sla_hours?: number;
}

export const stallsApi = {
  nearby: async (lat: number, lng: number, radius_m = 1500, signal?: AbortSignal) => {
    const env = await apiFetch<RawStallEnvelope>(
      `/v1/stalls/nearby?lat=${encodeURIComponent(lat)}&lng=${encodeURIComponent(
        lng,
      )}&radius_m=${encodeURIComponent(radius_m)}`,
      { signal },
    );
    return mapEnvelope(env);
  },
  search: async (q: string, signal?: AbortSignal) => {
    const env = await apiFetch<RawStallEnvelope>(`/v1/stalls/search?q=${encodeURIComponent(q)}`, {
      signal,
    });
    return mapEnvelope(env);
  },
  get: async (id: string, signal?: AbortSignal) => {
    const raw = await apiFetch<RawStall>(`/v1/stalls/${encodeURIComponent(id)}`, { signal });
    return mapStall(raw);
  },
  submit: (input: StallSubmissionInput, token: string) =>
    apiFetch<StallSubmissionResponse>('/v1/stalls/submissions', {
      method: 'POST',
      body: input,
      token,
    }),
};

// Exported for tests.
export const __test__ = { mapStall, mapEnvelope };
